"use client";

import {
	AcademicCapIcon,
	BoltIcon,
	CursorArrowRaysIcon,
} from "@heroicons/react/20/solid";
import { LocaleLink } from "@i18n/routing";
import { GenerateThenHighlightGradientText } from "@marketing/shared/components/generate-then-highlight-gradient-text";
import { useMarketingLandingVideoOverlay } from "@marketing/shared/components/marketing-landing-video-overlay-context";
import { landingStackedFadeDelay } from "@marketing/shared/lib/landing-stacked-fade";
import { landingTextGenerateChainMs } from "@marketing/shared/lib/landing-text-generate";
import {
	MARKETING_HERO_SCREENSHOT_IMAGE_SIZES,
	MARKETING_HEXTILE_PATTERN_LAYER_CLASS,
	MARKETING_LANDING_HERO_GLASS_INNER_CLASS,
	MARKETING_SITE_HEADER_HEIGHT_VAR,
	marketingHeroFillStyle,
} from "@marketing/shared/lib/marketing-layout-vars";
import { getMarketingLinkPrefetch } from "@marketing/shared/lib/marketing-route-prefetch";
import { IDENTITY_LIFECYCLE_SIGNALS } from "@repo/identity";
import { FadeInOnScroll } from "@repo/ui/components/custom/fade-in-on-scroll";
import {
	GlassBorder,
	GlassBorderPanel,
} from "@repo/ui/components/custom/glass-border";
import { Highlighter } from "@repo/ui/components/open-source/highlighter";
import { TextGenerateEffect } from "@repo/ui/components/react-bits/text-generate-effect";
import { cn } from "@repo/ui/lib/cn";
import { BorderBeam } from "@repo/ui/components/design-system/border-beam";
import { ClientFeatureErrorBoundary } from "@shared/components/client-feature-error-boundary";
import { InteractiveHexagonBackground } from "@shared/components/InteractiveHexagonBackground";
import {
	MarketingPrimaryCta as PrimaryCta,
	MarketingSecondaryCta as SecondaryCta,
} from "@shared/lib/cta/marketing-cta";
import { Play } from "lucide-react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import * as React from "react";
import { getMarketingLandingVideoSrc } from "../lib/landing-video/marketing-landing-video-src";
import { LandingLaunchCountdownBadge } from "./landing-launch-countdown-badge";
import { LandingVideoDialog } from "./landing-video-dialog";

const LANDING_IMG = "/images/landing-page";

const HERO_FILL_BELOW_HEADER = `calc(100svh - var(${MARKETING_SITE_HEADER_HEIGHT_VAR}, 7rem))`;

const SWIRLY_BREAKPOINT_TRANSITION_CLASS =
	"transition-all duration-500 ease-out motion-reduce:transition-none";

/** Narrow viewports: compact padding vs. desktop `large` footprint; no min width/height. */
const LANDING_HERO_MOBILE_CTA_CLASS =
	"max-sm:!min-h-0 max-sm:!min-w-0 max-sm:h-auto max-sm:w-auto max-sm:rounded-xl max-sm:px-5 max-sm:py-3";

/** Shared horizontal padding step for hero primary + secondary pair (`sm` / `md`); smallest uses {@link LANDING_HERO_MOBILE_CTA_CLASS} only. */
const LANDING_HERO_CTA_RESPONSIVE_PAD_CLASS = "sm:px-7 sm:py-4 md:px-8";

function SwirlyEmphasisPhrase({
	phrase,
	Icon,
}: {
	phrase: string;
	Icon: React.ComponentType<{ className?: string }>;
}) {
	return (
		<span className="inline-flex items-baseline font-extrabold tracking-wide">
			<Icon
				className="mr-1 inline size-4 shrink-0 translate-y-px fill-yellow-700 dark:fill-yellow-400"
				aria-hidden
			/>
			{phrase}
		</span>
	);
}

export function LandingHero() {
	const t = useTranslations("marketing.landing.hero");
	const headlineHighlight = t("headlineHighlight");
	const headlineRest = t("headlineRest");
	const subPrefix = t("subheadPrefix");
	const subStrong = t("subheadStrong");
	const subSuffix = t("subheadSuffix");
	const headlineHlMs = landingTextGenerateChainMs(headlineHighlight);
	const subPrefixChainMs = landingTextGenerateChainMs(subPrefix);
	const subStrongHlDefer =
		subPrefixChainMs + landingTextGenerateChainMs(subStrong);

	const [subStrongHighlighterReady, setSubStrongHighlighterReady] =
		React.useState(false);
	const [subCursorReveal, setSubCursorReveal] = React.useState(false);
	const { isOpen: videoOpen, setIsOpen: setVideoOpen } =
		useMarketingLandingVideoOverlay();
	const [videoBoundaryKey, setVideoBoundaryKey] = React.useState(0);

	const recoverLandingVideo = React.useCallback(() => {
		setVideoOpen(false);
		setVideoBoundaryKey((current) => current + 1);
	}, [setVideoOpen]);

	const handleSubStrongGenerateDone = React.useCallback(() => {
		setSubStrongHighlighterReady(true);
	}, []);

	const handleSubHighlightShow = React.useCallback(() => {
		setSubCursorReveal(true);
	}, []);

	return (
		<div
			className="relative flex flex-col overflow-x-visible select-none"
			style={marketingHeroFillStyle(HERO_FILL_BELOW_HEADER)}
		>
			<FadeInOnScroll className="flex min-h-0 flex-1 flex-col">
				<InteractiveHexagonBackground
					interactiveAriaLabel={t("interactiveBackgroundAria")}
					contentWrapperClassName="justify-center py-4 sm:py-6"
					patternLayerClassName={
						MARKETING_HEXTILE_PATTERN_LAYER_CLASS
					}
					overlays={
						<>
							<FadeInOnScroll
								delay={landingStackedFadeDelay(4)}
								rootMargin="0px"
								className={cn(
									"pointer-events-none absolute bottom-0 left-0 z-[1] hidden lg:left-4 lg:block xl:left-10",
								)}
							>
								<Image
									src={`${LANDING_IMG}/screenshots/battle.png`}
									alt={t("battleScreenshotAlt")}
									width={300}
									height={200}
									sizes={
										MARKETING_HERO_SCREENSHOT_IMAGE_SIZES
									}
									loading="lazy"
									fetchPriority="low"
									className={cn(
										"h-auto w-[120px] transition-all duration-500 lg:w-[196px] xl:w-[300px]",
									)}
									style={{
										transitionProperty: "width, left",
									}}
								/>
							</FadeInOnScroll>

							<FadeInOnScroll
								delay={landingStackedFadeDelay(4)}
								rootMargin="0px"
								className="pointer-events-none absolute bottom-0 right-0 z-[1] hidden lg:block"
							>
								<Image
									src={`${LANDING_IMG}/screenshots/menu.png`}
									alt={t("menuScreenshotAlt")}
									width={380}
									height={200}
									sizes={
										MARKETING_HERO_SCREENSHOT_IMAGE_SIZES
									}
									loading="lazy"
									fetchPriority="low"
									className={cn(
										"h-auto w-[200px] transition-all duration-500 lg:w-[250px] xl:w-[380px]",
									)}
									style={{
										transitionProperty: "width, right",
									}}
								/>
							</FadeInOnScroll>
						</>
					}
				>
					<div className="container relative overflow-x-visible text-center">
						<FadeInOnScroll>
							<GlassBorder
								variant="inner"
								radius="2xl"
								className="mx-auto mb-6 w-max sm:mb-8"
							>
								<GlassBorderPanel className="block w-max">
									<LandingLaunchCountdownBadge />
								</GlassBorderPanel>
							</GlassBorder>
						</FadeInOnScroll>

						<FadeInOnScroll delay={landingStackedFadeDelay(1)}>
							<h1 className="mx-auto max-w-3xl overflow-visible text-center font-display text-4xl font-medium tracking-tight text-slate-900 sm:text-7xl dark:text-white">
								<span className="relative isolate mx-auto inline-block max-w-full overflow-visible">
									<FadeInOnScroll
										delay={landingStackedFadeDelay(6)}
										rootMargin="0px"
										intersectionThreshold={0}
										className={cn(
											"pointer-events-none absolute top-0 left-0 z-[5] hidden overflow-visible lg:block",
										)}
									>
										<span
											className={cn(
												"block lg:translate-x-[80px] lg:-translate-y-[90px] xl:translate-x-[20px] xl:-translate-y-[100px]",
												SWIRLY_BREAKPOINT_TRANSITION_CLASS,
											)}
											style={{
												transitionProperty: "transform",
											}}
										>
											<Image
												src={`${LANDING_IMG}/swirly-arrow.png`}
												alt=""
												width={200}
												height={200}
												loading="lazy"
												className={cn(
													"h-auto w-[6.25rem] object-contain opacity-95 xl:w-[7.25rem]",
													SWIRLY_BREAKPOINT_TRANSITION_CLASS,
													"dark:invert dark:brightness-110",
												)}
												style={{
													transform:
														"scaleX(-1) rotate(10deg)",
													transformOrigin: "center",
													transitionProperty:
														"width, transform",
												}}
											/>
										</span>
									</FadeInOnScroll>
									<FadeInOnScroll
										delay={landingStackedFadeDelay(7)}
										rootMargin="0px"
										intersectionThreshold={0}
										className={cn(
											"pointer-events-none absolute top-0 right-0 z-[5] hidden overflow-visible lg:block",
										)}
									>
										<span
											className={cn(
												"block lg:-translate-x-[80px] lg:-translate-y-[90px] xl:-translate-x-[20px] xl:-translate-y-[100px]",
												SWIRLY_BREAKPOINT_TRANSITION_CLASS,
											)}
											style={{
												transitionProperty: "transform",
											}}
										>
											<Image
												src={`${LANDING_IMG}/swirly-arrow.png`}
												alt=""
												width={200}
												height={200}
												loading="lazy"
												className={cn(
													"h-auto w-[6.25rem] object-contain opacity-95 xl:w-[7.25rem]",
													SWIRLY_BREAKPOINT_TRANSITION_CLASS,
													"dark:invert dark:brightness-110",
												)}
												style={{
													transform: "rotate(10deg)",
													transformOrigin: "center",
													transitionProperty:
														"width, transform",
												}}
											/>
										</span>
									</FadeInOnScroll>
									<GlassBorder
										variant="inner"
										radius="4xl"
										className="relative z-10 mx-auto inline-block max-w-full"
									>
										<GlassBorderPanel
											as="span"
											className={cn(
												"inline-flex max-w-full flex-wrap items-baseline justify-center gap-x-2 gap-y-1 border border-white/80 px-6 py-5 md:px-12 md:py-10 shadow-sm backdrop-blur-sm dark:border-white/10",
												MARKETING_LANDING_HERO_GLASS_INNER_CLASS,
											)}
										>
											<GenerateThenHighlightGradientText
												as="span"
												words={headlineHighlight}
												className="relative inline text-center font-extrabold"
												fontBold={false}
												phraseGradientClassName="bg-gradient-to-r from-[#6366f1] via-[#9c40ff] to-[#b868ff] bg-clip-text font-extrabold text-transparent"
												phraseAnimatedGradient={{
													fromColor: "#6366f1",
													viaColor: "#9c40ff",
													toColor: "#b868ff",
												}}
												respectFadeIn
												duration={0.4}
												staggerDelay={0.03}
												highlighterLayers={[
													{
														action: "highlight",
														strokeWidth: 8,
														padding: 20,
														color: "rgba(99, 102, 241, 0.15)",
														isView: true,
													},
													{
														action: "underline",
														strokeWidth: 8,
														padding: 0,
														color: "rgba(99, 102, 241, 0.3)",
														isView: true,
													},
												]}
											/>
											<TextGenerateEffect
												as="span"
												words={headlineRest}
												delayBeforeAnimateMs={
													headlineHlMs
												}
												className="inline text-center font-display font-medium tracking-tight text-slate-900 dark:text-white"
												fontBold={false}
												respectFadeIn
												duration={0.4}
												staggerDelay={0.03}
											/>
											<BorderBeam
												borderWidth={2}
												duration={8}
												pathCornerRadiusFromGlass
												size={150}
											/>
										</GlassBorderPanel>
									</GlassBorder>
									<FadeInOnScroll
										delay={landingStackedFadeDelay(6)}
										rootMargin="0px"
										intersectionThreshold={0}
										className={cn(
											"pointer-events-none absolute top-[6%] left-0 z-[15] hidden w-max -translate-x-full overflow-visible pr-5 text-right leading-snug lg:block",
											"xl:top-[8%] xl:pr-7",
										)}
									>
										<span
											className={cn(
												"block max-w-[min(13rem,calc(48vw-9rem))] text-sm tracking-wide text-slate-700 xl:max-w-[16rem] xl:text-lg",
												"dark:text-neutral-300",
												"lg:translate-x-[85px] lg:-translate-y-[117px] xl:translate-x-[30px] xl:-translate-y-[138px]",
												SWIRLY_BREAKPOINT_TRANSITION_CLASS,
											)}
											style={{
												transitionProperty:
													"transform, max-width, font-size",
											}}
										>
											{t("swirlyArrowLeftBefore")}
											<SwirlyEmphasisPhrase
												phrase={t(
													"swirlyArrowLeftEmphasis",
												)}
												Icon={AcademicCapIcon}
											/>
											{t("swirlyArrowLeftAfter")}
										</span>
									</FadeInOnScroll>
									<FadeInOnScroll
										delay={landingStackedFadeDelay(7)}
										rootMargin="0px"
										intersectionThreshold={0}
										className={cn(
											"pointer-events-none absolute top-[6%] right-0 z-[15] hidden w-max translate-x-full overflow-visible pl-5 text-left leading-snug lg:block",
											"xl:top-[8%] xl:pl-7",
										)}
									>
										<span
											className={cn(
												"block max-w-[min(13rem,calc(48vw-9rem))] text-sm tracking-wide text-slate-700 xl:max-w-[16rem] xl:text-lg",
												"dark:text-neutral-300",
												"lg:-translate-x-[85px] lg:-translate-y-[117px] xl:-translate-x-[30px] xl:-translate-y-[138px]",
												SWIRLY_BREAKPOINT_TRANSITION_CLASS,
											)}
											style={{
												transitionProperty:
													"transform, max-width, font-size",
											}}
										>
											{t("swirlyArrowRightBefore")}
											<SwirlyEmphasisPhrase
												phrase={t(
													"swirlyArrowRightEmphasis",
												)}
												Icon={BoltIcon}
											/>
											{t("swirlyArrowRightAfter")}
										</span>
									</FadeInOnScroll>
								</span>
							</h1>
						</FadeInOnScroll>

						<FadeInOnScroll delay={landingStackedFadeDelay(2)}>
							<div className="mx-auto mt-6 max-w-3xl text-base tracking-wide text-slate-700 sm:text-lg dark:text-neutral-300">
								<GlassBorder
									variant="inner"
									radius="3xl"
									className="inline-block"
								>
									<GlassBorderPanel
										as="span"
										className={cn(
											"inline-block border border-white/80 px-4 py-4 max-w-[720px] shadow-sm backdrop-blur-sm dark:border-white/10",
											MARKETING_LANDING_HERO_GLASS_INNER_CLASS,
										)}
									>
										<TextGenerateEffect
											as="span"
											words={subPrefix}
											className="inline text-base tracking-wide text-slate-700 sm:text-lg dark:text-neutral-300"
											fontBold={false}
											respectFadeIn
											duration={0.4}
											staggerDelay={0.03}
										/>{" "}
										{subStrongHighlighterReady ? (
											<Highlighter
												action="highlight"
												strokeWidth={5}
												color="rgba(202, 138, 4, 0.15)"
												isView
												deferMs={0}
												onShow={handleSubHighlightShow}
											>
												<CursorArrowRaysIcon
													className={cn(
														"mb-1 mr-1 inline h-4 w-4 shrink-0 fill-yellow-700 transition-opacity duration-700 ease-out motion-reduce:transition-none dark:fill-yellow-400",
														subCursorReveal
															? "opacity-100"
															: "opacity-0",
													)}
													aria-hidden
												/>
												<strong>
													<span className="inline font-semibold tracking-wide text-slate-700 dark:text-neutral-300">
														{subStrong}
													</span>
												</strong>
											</Highlighter>
										) : (
											<>
												<CursorArrowRaysIcon
													className="mb-1 mr-1 inline h-4 w-4 shrink-0 fill-yellow-700 opacity-0 dark:fill-yellow-400"
													aria-hidden
												/>
												<strong>
													<TextGenerateEffect
														as="span"
														words={subStrong}
														delayBeforeAnimateMs={
															subPrefixChainMs
														}
														className="inline font-semibold tracking-wide text-slate-700 dark:text-neutral-300"
														fontBold={false}
														respectFadeIn
														duration={0.4}
														staggerDelay={0.03}
														onAnimationComplete={
															handleSubStrongGenerateDone
														}
													/>
												</strong>
											</>
										)}{" "}
										<TextGenerateEffect
											as="span"
											words={subSuffix}
											delayBeforeAnimateMs={
												subStrongHlDefer
											}
											className="inline text-base tracking-wide text-slate-700 sm:text-lg dark:text-neutral-300"
											fontBold={false}
											respectFadeIn
											duration={0.4}
											staggerDelay={0.03}
										/>
										<BorderBeam
											borderWidth={2}
											className="from-transparent via-[#9c40ff] to-[#ffaa40]"
											delay={5.2}
											duration={8}
											pathCornerRadiusFromGlass
											reverse
											size={75}
										/>
									</GlassBorderPanel>
								</GlassBorder>
							</div>
						</FadeInOnScroll>

						<FadeInOnScroll delay={landingStackedFadeDelay(3)}>
							<div className="mt-6 flex justify-center gap-x-6">
								{/* TEMP: swap to “See how” deep-link; keep video CTA code for later re-enable.
								<SecondaryCta
									asChild
									ctaSize="large"
									className={cn(
										LANDING_HERO_MOBILE_CTA_CLASS,
										LANDING_HERO_CTA_RESPONSIVE_PAD_CLASS,
									)}
									analyticsLocation="landing_hero"
									analyticsCta="see_how"
								>
									<LocaleLink href="/#solution" prefetch>
										See how
									</LocaleLink>
								</SecondaryCta>
								*/}
								<SecondaryCta
									ctaSize="large"
									className={cn(
										LANDING_HERO_MOBILE_CTA_CLASS,
										LANDING_HERO_CTA_RESPONSIVE_PAD_CLASS,
										"inline-flex items-center justify-center",
									)}
									analyticsLocation="landing_hero"
									analyticsCta="watch_video"
									onClick={(e) => {
										e.preventDefault();
										setVideoOpen(true);
									}}
								>
									<span className="inline-flex items-center gap-2 leading-none">
										<Play
											className="relative top-px block size-[1.125rem] shrink-0 text-indigo-300 dark:text-indigo-200"
											fill="currentColor"
											strokeWidth={0}
											aria-hidden
										/>
										{t("watchVideo")}
									</span>
								</SecondaryCta>

								<PrimaryCta
									asChild
									ctaSize="large"
									analyticsLocation="landing_hero"
									analyticsCta="waitlist"
									identityLifecycleSignal={
										IDENTITY_LIFECYCLE_SIGNALS.highIntentCta
									}
									identityLifecycleStorageKey="sq:identity:lifecycle:high_intent_cta:landing_hero"
									className={cn(
										LANDING_HERO_MOBILE_CTA_CLASS,
										"sm:min-w-[10rem]",
										LANDING_HERO_CTA_RESPONSIVE_PAD_CLASS,
									)}
								>
									<LocaleLink
										href="/waitlist"
										prefetch={getMarketingLinkPrefetch(
											"/waitlist",
										)}
									>
										{t("waitlistCta")}
									</LocaleLink>
								</PrimaryCta>
							</div>
						</FadeInOnScroll>
					</div>
				</InteractiveHexagonBackground>
			</FadeInOnScroll>

			<ClientFeatureErrorBoundary
				key={videoBoundaryKey}
				onError={recoverLandingVideo}
			>
				<LandingVideoDialog
					open={videoOpen}
					onOpenChange={setVideoOpen}
					videoUrl={getMarketingLandingVideoSrc()}
				/>
			</ClientFeatureErrorBoundary>
		</div>
	);
}
